import _ from "lodash"

import { DealId, DealLineId } from "shared/hubrise/ids"
import { DealLinePricing, IDeal, IDealLine, IDealLineSku } from "shared/hubrise/models"
import * as Deal from "shared/hubrise/utils/Deal"
import * as DealLine from "shared/hubrise/utils/DealLine"

import { Change } from "./types"
import type { OperationContext } from "./types"

export type DealLineOperations = {
  add(deal: IDeal, dealLine: IDealLine): void
  delete(dealId: DealId, dealLineId: DealLineId): void
  reorderSkus(dealId: DealId, dealLineId: DealLineId, dealLineSkus: Array<IDealLineSku>): void
  update<TField extends keyof IDealLine>(
    dealId: DealId,
    dealLineId: DealLineId,
    fieldName: TField,
    value: IDealLine[TField],
    options?: { collapse?: boolean },
  ): void
  updatePricing(
    dealId: DealId,
    dealLineId: DealLineId,
    pricing: DealLinePricing,
    options?: { collapse?: boolean },
  ): void
}

export default ({ catalog, pushState }: OperationContext): DealLineOperations => ({
  add(deal, dealLine) {
    const newCatalog = Deal.update(catalog, deal.id, "lines", [...deal.lines, dealLine])
    pushState(newCatalog, { model: "dealLine", operation: "add" })
  },

  delete(dealId, dealLineId) {
    const newCatalog = DealLine.doDelete(catalog, dealId, dealLineId)
    pushState(newCatalog, { model: "dealLine", operation: "delete", id: dealLineId })
  },

  reorderSkus(dealId, dealLineId, dealLineSkus) {
    const newCatalog = DealLine.update(catalog, dealId, dealLineId, "skus", dealLineSkus)
    const change: Change = {
      model: "dealLine",
      id: dealLineId,
      operation: "reorderSkus",
    }
    pushState(newCatalog, change, (previousChange) => _.isEqual(previousChange, change))
  },

  update(dealId, dealLineId, fieldName, value, { collapse = true } = {}) {
    const newCatalog = DealLine.update(catalog, dealId, dealLineId, fieldName, value)
    const change: Change = {
      model: "dealLine",
      id: dealLineId,
      operation: "update",
      fieldName,
    }
    pushState(newCatalog, change, (previousChange) => collapse && _.isEqual(previousChange, change))
  },

  updatePricing(dealId, dealLineId, pricing, { collapse = true } = {}) {
    const newCatalog = DealLine.updatePricing(catalog, dealId, dealLineId, pricing)
    const change: Change = {
      model: "dealLine",
      id: dealLineId,
      operation: "update",
      fieldName: "pricing",
    }
    pushState(newCatalog, change, (previousChange) => collapse && _.isEqual(previousChange, change))
  },
})
